import { useState, useCallback, useRef } from 'react';
import type { GeocodingResult, SearchLocation } from '@/types';
import { MAPBOX_TOKEN } from '@/constants';

interface UseMapboxGeocodingResult {
  search: (query: string) => Promise<GeocodingResult[]>;
  reverseGeocode: (lat: number, lng: number) => Promise<GeocodingResult | null>;
  isSearching: boolean;
  error: string | null;
}

export function useMapboxGeocoding(): UseMapboxGeocodingResult {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const search = useCallback(async (query: string): Promise<GeocodingResult[]> => {
    if (!query.trim() || query.length < 2) {
      return [];
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setError(null);

    try {
      const url = new URL(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`
      );
      url.searchParams.set('access_token', MAPBOX_TOKEN);
      url.searchParams.set('country', 'gb'); // UK only
      url.searchParams.set('types', 'postcode,place,locality,neighborhood,address');
      url.searchParams.set('limit', '5');
      url.searchParams.set('language', 'en');

      const response = await fetch(url.toString(), {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();

      const results: GeocodingResult[] = (data.features || []).map(
        (feature: {
          text: string;
          place_name: string;
          center: [number, number];
          place_type: string[];
          context?: Array<{ id: string; text: string }>;
        }) => {
          // Extract a cleaner name for display
          let name = feature.text;

          // For postcodes, include the area
          if (feature.place_type.includes('postcode')) {
            const locality = feature.context?.find(
              (c) => c.id.startsWith('locality') || c.id.startsWith('place')
            );
            if (locality) {
              name = `${feature.text}, ${locality.text}`;
            }
          }

          return {
            name,
            fullAddress: feature.place_name,
            location: {
              longitude: feature.center[0],
              latitude: feature.center[1],
            } as SearchLocation,
          };
        }
      );

      return results;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was cancelled, not an error
        return [];
      }

      const message = err instanceof Error ? err.message : 'Search failed';
      setError(message);
      console.error('Geocoding error:', err);
      return [];
    } finally {
      setIsSearching(false);
    }
  }, []);

  const reverseGeocode = useCallback(async (lat: number, lng: number): Promise<GeocodingResult | null> => {
    setIsSearching(true);
    setError(null);

    try {
      const url = new URL(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`
      );
      url.searchParams.set('access_token', MAPBOX_TOKEN);
      url.searchParams.set('types', 'postcode,place,locality');
      url.searchParams.set('limit', '1');
      url.searchParams.set('language', 'en');

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Reverse geocoding request failed');
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        // Prefer postcode if available
        const name = feature.place_type?.includes('postcode')
          ? feature.text
          : feature.text || feature.place_name?.split(',')[0] || 'Your location';

        return {
          name,
          fullAddress: feature.place_name || name,
          location: { latitude: lat, longitude: lng },
        };
      }

      return { name: 'Your location', fullAddress: 'Your location', location: { latitude: lat, longitude: lng } };
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      return { name: 'Your location', fullAddress: 'Your location', location: { latitude: lat, longitude: lng } };
    } finally {
      setIsSearching(false);
    }
  }, []);

  return {
    search,
    reverseGeocode,
    isSearching,
    error,
  };
}
